import React, { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import StaticSeo from '../components/StaticSeo.jsx';
import { HARDNESS_BANDS, classifyHardness, mgLToGpg } from '../data/water.js';
// s135 (DECISIONS #57): covered cities' ZIPs resolve to the utility's PUBLISHED figure (same as /water/rankings);
// the older ZIP3 table and state fallback below are planning estimates and are labelled as such (#22).
import { ZIP_CITY, ZIP3_CITY, ZIP_CITIES } from '../data/zipHardness.js';

// ─────────────────────────────────────────────────────────────────────────
// HARD WATER SEVERITY SCORER
// Window Washing Guide — Tool 02 of 09
// ─────────────────────────────────────────────────────────────────────────
//
// Methodology (s135, DECISIONS #57): covered-city ZIPs → published figures (zipHardness.js). The tables below are
// legacy planning estimates (no document behind them) and render as estimates only.
// and EPA municipal water reports. Values shown are typical municipal supply
// for the dominant utility in each ZIP3 region, expressed in:
//   • mg/L CaCO₃  (the standard chemistry unit)
//   • gpg         (grains per gallon, the trade unit)
//
// Severity scoring follows USGS classification (mg/L CaCO₃):
//   0–60     soft         no action needed
//   61–120   moderate     distilled rinse on dark glass
//   121–180  hard         distilled rinse always
//   181–250  very hard    distilled rinse + monthly maintenance
//   251+     extremely hard   DI filter or pure-water system
//
// ─────────────────────────────────────────────────────────────────────────

// ZIP3 → hardness lookup. Covers ~120 of the most populous ZIP3 regions
// across the US. For unmapped ZIPs we fall back to state-level averages.
// Values are mg/L CaCO₃ planning estimates; not traceable to a document, so never labelled as a utility figure.
const ZIP3_HARDNESS = {
  // Northeast — generally soft (surface water from reservoirs)
  '100': 60, '101': 60, '102': 60, '103': 65, '104': 65, '110': 70, '111': 70,
  '112': 70, '113': 75, '114': 75, '115': 80, '116': 85, '117': 90, '118': 95,
  '119': 100, '120': 105, '121': 110, '122': 115, '123': 120,
  '019': 45, '020': 50, '021': 50, '022': 55, '023': 55, '024': 60, '025': 65,
  '026': 70, '027': 75, '028': 80, '029': 85,
  '060': 90, '061': 95, '062': 100, '063': 105, '064': 110, '065': 115,
  '070': 110, '071': 115, '072': 120, '073': 125, '074': 130, '075': 135,
  '076': 140, '077': 145, '078': 150, '079': 155,
  '080': 130, '081': 135, '082': 140, '083': 145, '084': 150,

  // Mid-Atlantic — moderate
  '190': 130, '191': 130, '192': 135, '193': 140, '194': 145, '195': 150,
  '196': 155, '197': 160, '198': 165, '199': 170,
  '200': 145, '201': 150, '202': 155, '203': 160, '204': 165, '205': 170,
  '206': 175, '207': 180, '208': 185, '209': 190,
  '210': 165, '211': 170, '212': 175, '213': 180, '214': 185, '215': 190,
  '216': 195, '217': 200, '218': 205, '219': 210,

  // Southeast — variable, mostly soft to moderate
  '300': 35, '301': 35, '302': 40, '303': 40, '304': 45, '305': 45,
  '306': 50, '307': 55, '308': 60, '309': 65, '310': 70, '311': 75,
  '320': 130, '321': 135, '322': 140, '323': 145, '324': 150, '325': 155,
  '326': 160, '327': 165, '328': 170, '329': 175,
  '330': 195, '331': 200, '332': 205, '333': 210, '334': 215, '335': 220,
  '336': 225, '337': 230, '338': 235, '339': 240,
  '270': 40, '271': 45, '272': 50, '273': 55, '274': 60, '275': 65,
  '276': 70, '277': 75, '278': 80, '279': 85,
  '280': 30, '281': 35, '282': 40, '283': 45, '284': 50, '285': 55,
  '286': 60, '287': 65, '288': 70, '289': 75,

  // Midwest — hard to extremely hard (limestone aquifers)
  '600': 195, '601': 200, '602': 205, '603': 210, '604': 215, '605': 220,
  '606': 225, '607': 230, '608': 235, '609': 240, '610': 245, '611': 250,
  '612': 255, '613': 260, '614': 265, '615': 270, '616': 275, '617': 280,
  '618': 285, '619': 290,
  '480': 165, '481': 170, '482': 175, '483': 180, '484': 185, '485': 190,
  '486': 195, '487': 200, '488': 205, '489': 210,
  '430': 180, '431': 185, '432': 190, '433': 195, '434': 200, '435': 205,
  '436': 210, '437': 215, '438': 220, '439': 225,
  '550': 145, '551': 150, '553': 155, '554': 160, '555': 165, '556': 170,
  '557': 175, '558': 180, '559': 185,
  '630': 230, '631': 235, '632': 240, '633': 245, '634': 250, '635': 255,
  '636': 260, '637': 265, '638': 270, '639': 275,
  '640': 245, '641': 250, '644': 255, '645': 260, '646': 265, '647': 270,
  '648': 275, '649': 280, '650': 285, '651': 290, '652': 295, '653': 300,
  '654': 305, '655': 310, '656': 315, '657': 320,
  '660': 275, '661': 280, '662': 285, '664': 290, '665': 295, '666': 300,
  '667': 305, '668': 310, '669': 315, '670': 320, '671': 325, '672': 330,
  '673': 335, '674': 340, '675': 345, '676': 350, '677': 355, '678': 360,
  '679': 365,

  // South Central — extremely hard
  '750': 285, '751': 290, '752': 295, '753': 300, '754': 305, '755': 310,
  '756': 315, '757': 320, '758': 325, '759': 330,
  '770': 175, '771': 180, '772': 185, '773': 190, '774': 195, '775': 200,
  '776': 205, '777': 210, '778': 215, '779': 220,
  '780': 245, '781': 250, '782': 255, '783': 260, '784': 265, '785': 270,
  '786': 275, '787': 280, '788': 285, '789': 290,
  '730': 200, '731': 205, '734': 210, '735': 215, '736': 220, '737': 225,
  '738': 230, '739': 235, '740': 240, '741': 245, '743': 250, '744': 255,
  '745': 260, '746': 265, '747': 270, '748': 275, '749': 280,

  // Mountain West — extremely hard (Phoenix, Vegas, SLC)
  '850': 320, '851': 325, '852': 330, '853': 335, '855': 340, '856': 345,
  '857': 350, '859': 355, '860': 360, '863': 365, '864': 370, '865': 375,
  '870': 145, '871': 150, '873': 155, '874': 160, '875': 165, '877': 170,
  '878': 175, '879': 180, '880': 185, '881': 190, '882': 195, '883': 200,
  '884': 205,
  '800': 175, '801': 180, '802': 185, '803': 190, '804': 195, '805': 200,
  '806': 205, '807': 210, '808': 215, '809': 220, '810': 225, '811': 230,
  '812': 235, '813': 240, '814': 245, '815': 250, '816': 255,
  '840': 290, '841': 295, '842': 300, '843': 305, '844': 310, '845': 315,
  '846': 320, '847': 325,
  '890': 305, '891': 310, '893': 315, '894': 320, '895': 325, '897': 330,
  '898': 335,

  // Pacific — soft (Seattle, Portland) to very hard (LA, SF Bay)
  '900': 165, '901': 170, '902': 175, '903': 180, '904': 185, '905': 190,
  '906': 195, '907': 200, '908': 205,
  '910': 175, '911': 180, '912': 185, '913': 190, '914': 195, '915': 200,
  '916': 205, '917': 210, '918': 215,
  '920': 165, '921': 170, '922': 175, '923': 180, '924': 185, '925': 190,
  '926': 195, '927': 200, '928': 205,
  '940': 105, '941': 110, '943': 115, '944': 120, '945': 125, '946': 130,
  '947': 135, '948': 140, '949': 145, '950': 150, '951': 155, '952': 160,
  '953': 165, '954': 170, '955': 175, '956': 180, '957': 185, '958': 190,
  '959': 195, '960': 200, '961': 205,
  '970': 35, '971': 40, '972': 45, '973': 50, '974': 55, '975': 60,
  '976': 65, '977': 70, '978': 75, '979': 80,
  '980': 30, '981': 35, '982': 40, '983': 45, '984': 50, '985': 55,
  '986': 60, '988': 65, '989': 70, '990': 75, '991': 80, '992': 85,
  '993': 90, '994': 95,

  // ==========================================================================
  // City-page coverage expansion (session 32). Each ZIP3 prefix below is
  // used by at least one city in src/data/cities/. Hardness values come
  // from the parent city's verified municipal CCR reading; when multiple
  // cities share a ZIP3 we use a population-weighted mean rounded to 5 mg/L.
  // Sourcing the tool from the city-page hardness table keeps the two
  // datasets in lockstep — a reader who reads the Charlotte page and then
  // enters a Charlotte ZIP gets the same hardness number on the scorer.
  // ==========================================================================

  // MASSACHUSETTS
  '011': 95, '016': 110, '018': 65,
  // NEW HAMPSHIRE
  '030': 160, '031': 145, '033': 160, '038': 145,
  // MAINE
  '041': 100, '042': 165, '044': 165,
  // VERMONT
  '054': 110, '057': 175,
  // CONNECTICUT
  '066': 90, '067': 110, '069': 90,
  // NEW JERSEY
  '085': 145, '086': 130,
  // NEW YORK (outside NYC metro already covered above)
  '107': 65, '132': 140, '142': 130, '146': 115,
  // PENNSYLVANIA (outside the Philly/Pittsburgh metros)
  '152': 125, '165': 135, '176': 260, '181': 220,
  // VIRGINIA
  '222': 120, '223': 130, '232': 85, '233': 130, '234': 130, '235': 125,
  '236': 130,
  // WEST VIRGINIA
  '253': 185, '254': 185, '257': 195, '260': 195, '265': 175,
  // SOUTH CAROLINA
  '292': 70, '294': 90, '297': 80,
  // GEORGIA (outside Atlanta metro)
  '314': 195, '319': 95,
  // ALABAMA
  '352': 70, '354': 95, '358': 105, '361': 110, '366': 90,
  // TENNESSEE
  '370': 120, '372': 120, '374': 115, '379': 115, '381': 95,
  // MISSISSIPPI
  '386': 95, '392': 140, '394': 130, '395': 110,
  // KENTUCKY
  '402': 130, '405': 120, '410': 120, '421': 200, '423': 125,
  // OHIO (outside Columbus metro already covered above)
  '441': 130, '443': 165, '452': 155, '454': 285,
  // INDIANA
  '460': 150, '462': 130, '463': 150, '464': 150, '468': 130, '474': 170,
  '477': 130,
  // MICHIGAN (outside Detroit metro already covered above)
  '495': 125,
  // IOWA
  '503': 310, '511': 285, '522': 275, '524': 250, '528': 230,
  // WISCONSIN
  '531': 145, '532': 150, '534': 150, '537': 250, '543': 150,
  // SOUTH DAKOTA
  '570': 220, '571': 180, '572': 230, '574': 240, '577': 140,
  // NORTH DAKOTA
  '580': 115, '581': 110, '582': 115, '585': 175, '587': 195,
  // MONTANA
  '591': 260, '594': 200, '597': 200, '598': 175,
  // ILLINOIS (downstate, outside Chicago metro)
  '627': 235,
  // MISSOURI (Springfield only — KC and STL covered above)
  '658': 85,
  // NEBRASKA
  '680': 175, '681': 180, '685': 200, '688': 265,
  // LOUISIANA
  '701': 115, '705': 230, '706': 245, '708': 160, '711': 195,
  // ARKANSAS
  '722': 120, '724': 195, '727': 140, '729': 160,
  // TEXAS (Fort Worth + El Paso, outside DFW and Houston metro)
  '761': 215, '762': 215, '799': 420,
  // WYOMING
  '820': 235, '826': 230, '827': 300, '829': 240,
  // IDAHO
  '834': 270, '836': 295, '837': 310,
  // CALIFORNIA (Fresno — Central Valley)
  '937': 280,
  // HAWAII
  '967': 120, '968': 95,
  // ALASKA
  '995': 85, '996': 180, '997': 170, '998': 60,
};

// State-level fallback averages (mg/L CaCO₃) when ZIP3 isn't in the table.
// First-digit ZIP → state grouping, approximate.
const ZIP1_FALLBACK = {
  '0': 75,  // New England
  '1': 90,  // NY/PA
  '2': 175, // DC/VA/MD/WV
  '3': 160, // FL/GA/AL/TN
  '4': 195, // IN/OH/KY/MI
  '5': 175, // IA/MN/MT/ND/SD/WI
  '6': 240, // IL/KS/MO/NE
  '7': 235, // AR/LA/OK/TX
  '8': 245, // AZ/CO/ID/NM/NV/UT/WY
  '9': 130, // CA/HI/OR/WA/AK
};

const NOTABLE_CITIES = {
  // Northeast
  '100': 'Manhattan, NY', '021': 'Boston, MA', '107': 'Yonkers, NY',
  '132': 'Syracuse, NY', '142': 'Buffalo, NY', '146': 'Rochester, NY',
  '011': 'Springfield, MA', '016': 'Worcester, MA', '018': 'Lowell, MA',
  '030': 'Nashua, NH', '031': 'Manchester, NH', '033': 'Concord, NH',
  '038': 'Portsmouth, NH', '041': 'Portland, ME', '042': 'Lewiston, ME',
  '044': 'Bangor, ME', '054': 'Burlington, VT', '057': 'Rutland, VT',
  '066': 'Bridgeport, CT', '067': 'Waterbury, CT', '069': 'Stamford, CT',

  // Mid-Atlantic
  '190': 'Philadelphia, PA', '152': 'Pittsburgh, PA', '165': 'Erie, PA',
  '176': 'Lancaster, PA', '181': 'Allentown, PA', '085': 'Princeton, NJ',
  '086': 'Trenton, NJ', '212': 'Baltimore, MD',
  '222': 'Arlington, VA', '223': 'Alexandria, VA', '232': 'Richmond, VA',
  '233': 'Chesapeake, VA', '234': 'Virginia Beach, VA', '235': 'Norfolk, VA',
  '236': 'Newport News, VA', '253': 'Charleston, WV', '254': 'Martinsburg, WV',
  '257': 'Huntington, WV', '260': 'Wheeling, WV', '265': 'Morgantown, WV',

  // Southeast
  '300': 'Atlanta, GA', '314': 'Savannah, GA', '319': 'Columbus, GA',
  '280': 'Charlotte, NC', '292': 'Columbia, SC', '294': 'Charleston, SC',
  '297': 'Rock Hill, SC', '331': 'Miami, FL',
  '352': 'Birmingham, AL', '354': 'Tuscaloosa, AL', '358': 'Huntsville, AL',
  '361': 'Montgomery, AL', '366': 'Mobile, AL',
  '370': 'Clarksville, TN', '372': 'Nashville, TN', '374': 'Chattanooga, TN',
  '379': 'Knoxville, TN', '381': 'Memphis, TN',
  '386': 'Southaven, MS', '392': 'Jackson, MS', '394': 'Hattiesburg, MS',
  '395': 'Gulfport, MS',
  '402': 'Louisville, KY', '405': 'Lexington, KY', '410': 'Covington, KY',
  '421': 'Bowling Green, KY', '423': 'Owensboro, KY',

  // Midwest
  '432': 'Columbus, OH', '441': 'Cleveland, OH', '443': 'Akron, OH',
  '452': 'Cincinnati, OH', '454': 'Dayton, OH',
  '460': 'Carmel, IN', '462': 'Indianapolis, IN', '463': 'Hammond, IN',
  '464': 'Gary, IN', '468': 'Fort Wayne, IN', '474': 'Bloomington, IN',
  '477': 'Evansville, IN',
  '480': 'Detroit, MI', '495': 'Grand Rapids, MI',
  '503': 'Des Moines, IA', '511': 'Sioux City, IA', '522': 'Iowa City, IA',
  '524': 'Cedar Rapids, IA', '528': 'Davenport, IA',
  '531': 'Kenosha, WI', '532': 'Milwaukee, WI', '534': 'Racine, WI',
  '537': 'Madison, WI', '543': 'Green Bay, WI',
  '551': 'Minneapolis, MN', '606': 'Chicago, IL', '627': 'Springfield, IL',
  '631': 'St. Louis, MO', '658': 'Springfield, MO',
  '660': 'Kansas City, KS',
  '570': 'Brookings, SD', '571': 'Sioux Falls, SD', '572': 'Watertown, SD',
  '574': 'Aberdeen, SD', '577': 'Rapid City, SD',
  '580': 'West Fargo, ND', '581': 'Fargo, ND', '582': 'Grand Forks, ND',
  '585': 'Bismarck, ND', '587': 'Minot, ND',
  '591': 'Billings, MT', '594': 'Great Falls, MT', '597': 'Bozeman, MT',
  '598': 'Missoula, MT',
  '680': 'Bellevue, NE', '681': 'Omaha, NE', '685': 'Lincoln, NE',
  '688': 'Grand Island, NE',

  // South Central
  '750': 'Dallas, TX', '761': 'Fort Worth, TX', '770': 'Houston, TX',
  '780': 'Austin, TX', '799': 'El Paso, TX',
  '701': 'New Orleans, LA', '705': 'Lafayette, LA', '706': 'Lake Charles, LA',
  '708': 'Baton Rouge, LA', '711': 'Shreveport, LA',
  '722': 'Little Rock, AR', '724': 'Jonesboro, AR', '727': 'Fayetteville, AR',
  '729': 'Fort Smith, AR',
  '730': 'Oklahoma City, OK',

  // Mountain West
  '802': 'Denver, CO', '820': 'Cheyenne, WY', '826': 'Casper, WY',
  '827': 'Gillette, WY', '829': 'Rock Springs, WY',
  '834': 'Idaho Falls, ID', '836': 'Meridian, ID', '837': 'Boise, ID',
  '840': 'Salt Lake City, UT', '850': 'Phoenix, AZ', '894': 'Las Vegas, NV',

  // Pacific
  '900': 'Los Angeles, CA', '937': 'Fresno, CA', '941': 'San Francisco, CA',
  '972': 'Portland, OR', '981': 'Seattle, WA',
  '967': 'Hilo / Kailua, HI', '968': 'Honolulu, HI',
  '995': 'Anchorage, AK', '996': 'Wasilla, AK', '997': 'Fairbanks, AK',
  '998': 'Juneau, AK',
};

// ─── classification helpers ──────────────────────────────────────────────
// s73 — these were the site's ONLY correct copy of the bands, and s72 built
// src/data/water.js by copying them here. Two correct copies are still two
// copies: verify-water-atlas.mjs existed purely to re-parse this function and
// shout if the two ever drifted. They can't now — this reads the same array
// the Atlas does, so the gate's drift check has been replaced by a check that
// no second copy exists at all. `tier` stays a 0–4 index because
// RECOMMENDATIONS and the scale strip below are keyed by position.
function classify(mgL) {
  const band = classifyHardness(mgL) || HARDNESS_BANDS[HARDNESS_BANDS.length - 1];
  return { tier: HARDNESS_BANDS.indexOf(band), label: band.label, code: band.code };
}

function mgLtoGpg(mgL) {
  return mgLToGpg(mgL).toFixed(1);
}

// Recommendation copy — voice rules: opinionated, specific, no AI tells
const RECOMMENDATIONS = {
  0: {
    headline: 'You won the geography lottery.',
    summary:
      'Your tap water is soft enough that you can wash and rinse with it directly. No distilled water needed for normal residential cleaning. Spend the savings on a better squeegee.',
    actions: [
      { k: 'Wash water', v: 'Tap is fine.' },
      { k: 'Rinse water', v: 'Tap is fine.' },
      { k: 'Soap ratio', v: 'One drop Dawn per quart.' },
      { k: 'Frequency', v: 'Quarterly. You will not see buildup between cleans.' },
    ],
    flag: 'No special equipment needed.',
  },
  1: {
    headline: 'Borderline. Watch your dark-tinted glass.',
    summary:
      'Tap is workable for most surfaces, but on dark or low-angle glass you will start to see faint mineral haze after the second or third wash. Switch to distilled for the rinse step on anything that catches direct light.',
    actions: [
      { k: 'Wash water', v: 'Tap is fine.' },
      { k: 'Rinse water', v: 'Distilled on dark/tinted panes.' },
      { k: 'Soap ratio', v: 'One drop Dawn per quart.' },
      { k: 'Frequency', v: 'Quarterly. Inspect dark glass for haze.' },
    ],
    flag: 'One gallon of distilled water per session is plenty.',
  },
  2: {
    headline: 'Distilled rinse, every time.',
    summary:
      'You are in the band where tap-water cleaning leaves a visible film within minutes of drying. Wash with tap (the surfactant holds the minerals in suspension), but rinse with distilled. This is the single change that fixes 90% of "I cleaned it and it still looks bad" complaints in your region.',
    actions: [
      { k: 'Wash water', v: 'Tap with soap is fine.' },
      { k: 'Rinse water', v: 'Distilled, every clean.' },
      { k: 'Soap ratio', v: 'One drop Dawn per quart. Do not increase to compensate.' },
      { k: 'Frequency', v: 'Monthly on south- and west-facing glass.' },
    ],
    flag: 'Two gallons distilled per cleaning session for an average home.',
  },
  3: {
    headline: 'You need a system, not a habit.',
    summary:
      'At this hardness, mineral deposits begin etching the glass surface itself within 18 to 24 months of routine cleaning with tap water. The film is no longer "on" the glass — it has bonded to it. Distilled water for both wash and rinse, or invest in a deionizing filter cartridge that fits a standard hose.',
    actions: [
      { k: 'Wash water', v: 'Distilled, or DI-filtered.' },
      { k: 'Rinse water', v: 'Distilled, or DI-filtered.' },
      { k: 'Soap ratio', v: 'One drop Dawn per quart. Strictly.' },
      { k: 'Frequency', v: 'Monthly. Quarterly vinegar maintenance pass.' },
    ],
    flag: 'A $40 DI cartridge from XERO or IPC pays for itself in two months.',
  },
  4: {
    headline: 'Pure-water cleaning is not optional here.',
    summary:
      'You are in the hardest band in North America. Phoenix, Las Vegas, parts of Texas and the upper Midwest. At this level, calcium carbonate precipitates out of any water that touches your glass and dries — the spots you see are essentially limestone, growing one molecular layer at a time. Tap water cannot be part of your final pass under any circumstance.',
    actions: [
      { k: 'Wash water', v: 'DI-filtered or RO-filtered. Period.' },
      { k: 'Rinse water', v: 'Same. No tap contact at any stage.' },
      { k: 'Soap ratio', v: 'One drop Dawn per quart. Or skip soap on a DI/water-fed system.' },
      { k: 'Frequency', v: 'Monthly minimum. Vinegar maintenance every 6 weeks.' },
    ],
    flag: 'Existing spots may already be etched. The Solution Calculator includes a CLR-based restoration protocol.',
  },
};

// Pure scoring helper. Extracted from handleSubmit so the URL-prefill
// effect can call it without depending on component state closures, and
// so the input-validation + lookup logic stays in one place. Returns
// either { ok: true, result } or { ok: false, error }.
function scoreZip(rawZip) {
  const trimmed = String(rawZip || '').trim();
  if (!/^\d{5}$/.test(trimmed)) {
    return { ok: false, error: 'Five digits, please.' };
  }
  const zip3 = trimmed.slice(0, 3);
  const zip1 = trimmed.slice(0, 1);
  // 1. A covered city's ZIP (or a ZIP3 only one covered city uses): the utility's published figure, or nothing.
  const key = ZIP_CITY[trimmed] || ZIP3_CITY[zip3];
  if (key) {
    const pc = ZIP_CITIES[key];
    const [stateSlug, citySlug] = key.split('/');
    if (!pc.fig) {
      return { ok: false, error: `${pc.by || `The ${pc.c} utility`} publishes no hardness figure we could read, so there is nothing to score here. The ${pc.c} page explains what to look for instead.`, cityLink: `/states/${stateSlug}/${citySlug}`, cityName: pc.c };
    }
    // Ranges are graded by their high end (the method panel's rule) and printed as published, never as a midpoint.
    const mgLp = pc.hi;
    const isRange = pc.lo !== pc.hi;
    const clsp = classify(mgLp);
    return { ok: true, result: { zip: trimmed, mgL: mgLp, display: isRange ? pc.fig : null,
      gpg: isRange ? `${mgLtoGpg(pc.lo)}–${mgLtoGpg(pc.hi)}` : mgLtoGpg(mgLp), classification: clsp,
      city: `${pc.c}, ${pc.st}`, precision: 'published', by: pc.by, cityLink: `/states/${stateSlug}/${citySlug}`, recommendation: RECOMMENDATIONS[clsp.tier] } };
  }
  let mgL = ZIP3_HARDNESS[zip3];
  let precision = 'estimate';
  if (mgL === undefined) {
    mgL = ZIP1_FALLBACK[zip1];
    precision = 'regional';
  }
  if (mgL === undefined) {
    return { ok: false, error: 'That ZIP is not in our dataset. Try a nearby one.' };
  }
  const cls = classify(mgL);
  const city =
    NOTABLE_CITIES[zip3] ||
    (precision === 'estimate' ? `ZIP region ${zip3}xx` : 'your region');
  return {
    ok: true,
    result: {
      zip: trimmed,
      mgL,
      gpg: mgLtoGpg(mgL),
      classification: cls,
      city,
      precision,
      recommendation: RECOMMENDATIONS[cls.tier],
    },
  };
}

// ─── component ───────────────────────────────────────────────────────────
export default function HardWaterScorer() {
  const [zip, setZip] = useState('');
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [errLink, setErrLink] = useState(null); // s135 #57: city page for a ZIP whose utility publishes no figure
  const [dark, setDark] = useState(false);
  const [animateNum, setAnimateNum] = useState(0);
  // Whether the currently-displayed result was prefilled from a ?zip=
  // deep-link rather than a manual scan. Drives the small attribution
  // chip at the top of ResultPanel that gives the visitor a one-tap
  // "open this tool fresh" affordance and tells them where the result
  // came from. Cleared on reset() and on any subsequent manual scan
  // (handleSubmit) — a fresh user-initiated run supersedes the
  // deep-link provenance.
  const [cameFromDeepLink, setCameFromDeepLink] = useState(false);
  const inputRef = useRef(null);
  // URL prefill: state pages and city pages deep-link into this tool with
  // ?zip=12345 so a tap on "Score this city's hardness" lands you on a
  // pre-run result instead of an empty input. Keeps the tool's standalone
  // route working unchanged when no query param is present.
  const [searchParams, setSearchParams] = useSearchParams();

  // Animate the score counting up when result changes
  useEffect(() => {
    if (!result) return;
    const target = result.mgL;
    const duration = 900;
    const start = performance.now();
    let raf;
    const tick = (now) => {
      const t = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - t, 3);
      setAnimateNum(Math.round(target * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [result]);

  // Auto-run on mount if a valid ?zip=12345 is present in the URL.
  // The ZIP is sticky in the input field after auto-run so reset() works
  // naturally; the query param is also cleared to keep the URL tidy after
  // the first render and to make the back button feel right (you go back
  // to the page that linked you, not to a fresh re-run of the same scan).
  // The `cameFromDeepLink` flag is set here so ResultPanel knows to
  // render the attribution chip — without it the visitor sees a scored
  // result with no breadcrumb back to where they came from.
  useEffect(() => {
    const qp = searchParams.get('zip');
    if (!qp) return;
    if (!/^\d{5}$/.test(qp)) return;
    const scored = scoreZip(qp);
    if (scored.ok) {
      setZip(qp);
      setAnimateNum(0);
      setResult(scored.result);
      setCameFromDeepLink(true);
    } else {
      // s135 #57: a city-page ZIP link to a city with no published figure explains itself instead of landing blank.
      setZip(qp); setError(scored.error); setErrLink(scored.cityLink ? { to: scored.cityLink, name: scored.cityName } : null);
    }
    // Clear the query param after consuming it. We intentionally pass
    // replace: true so the cleared URL doesn't add a history entry.
    const next = new URLSearchParams(searchParams);
    next.delete('zip');
    setSearchParams(next, { replace: true });
    // We only run this effect once on mount; the URLSearchParams object
    // identity changes after setSearchParams, which would re-fire the
    // effect, but the guard on qp short-circuits that re-entry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function handleSubmit(e) {
    if (e) e.preventDefault();
    setError(''); setErrLink(null);
    const scored = scoreZip(zip);
    if (!scored.ok) {
      setError(scored.error);
      setErrLink(scored.cityLink ? { to: scored.cityLink, name: scored.cityName } : null);
      inputRef.current?.focus();
      return;
    }
    setAnimateNum(0);
    setResult(scored.result);
    // A manual scan supersedes any prior deep-link provenance — the
    // result the visitor is now seeing came from their own input, so
    // the attribution chip should go away.
    setCameFromDeepLink(false);
  }

  function reset() {
    setResult(null);
    setZip('');
    setError(''); setErrLink(null);
    setCameFromDeepLink(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  }

  // ─── theme tokens ──────────────────────────────────────────────────────
  const T = dark
    ? {
        bg: '#1A1D1C',
        surface: '#22262A',
        ink: '#F4F1EA',
        inkSoft: '#B8B2A4',
        inkFaint: '#6B6960',
        rule: '#2E3236',
        sienna: '#D67148',
        sage: '#9CA89A',
      }
    : {
        bg: '#F4F1EA',
        surface: '#FAF7F1',
        ink: '#1A1D1C',
        inkSoft: '#5A5A52',
        inkFaint: '#A39E91',
        rule: '#D9D3C5',
        sienna: '#B5532A',
        sage: '#9CA89A',
      };

  const fontDisplay = "'Fraunces', 'Times New Roman', serif";
  const fontBody = "'Inter Tight', 'Helvetica Neue', sans-serif";
  const fontMono = "'JetBrains Mono', 'Courier New', monospace";

  // ─── render ────────────────────────────────────────────────────────────
  return (
    <div
      style={{
        minHeight: '100vh',
        background: T.bg,
        color: T.ink,
        fontFamily: fontBody,
        transition: 'background 0.3s, color 0.3s',
        padding: '32px 20px 80px',
      }}
    >
      <StaticSeo schemaType="webApplication" path="/tools/hard-water-scorer" />
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,300;9..144,400;9..144,500;9..144,600&family=Inter+Tight:wght@300;400;500;600&family=JetBrains+Mono:wght@400;500&display=swap"
      />

      <style>{`
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(12px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes fillBar {
          from { transform: scaleX(0); }
          to   { transform: scaleX(1); }
        }
        .reveal { animation: fadeUp 0.6s cubic-bezier(.2,.7,.3,1) both; }
        .reveal-1 { animation-delay: 0.05s; }
        .reveal-2 { animation-delay: 0.15s; }
        .reveal-3 { animation-delay: 0.25s; }
        .reveal-4 { animation-delay: 0.35s; }
        .reveal-5 { animation-delay: 0.45s; }
        .bar-fill { transform-origin: left; animation: fillBar 1.1s cubic-bezier(.2,.7,.3,1) 0.2s both; }

        input.zip-input::placeholder { color: ${T.inkFaint}; }
        input.zip-input:focus { outline: none; border-bottom-color: ${T.sienna}; }

        .pill-btn:hover { background: ${T.ink}; color: ${T.bg}; }
        .ghost-btn:hover { color: ${T.sienna}; }

        .scale-tier {
          transition: opacity 0.3s, transform 0.3s;
        }
        .scale-tier.dim { opacity: 0.28; }
        .scale-tier.active { transform: translateY(-2px); }

        @media (max-width: 720px) {
          .grid-2col { grid-template-columns: 1fr !important; }
          .display-num { font-size: 96px !important; }
          .h1-title { font-size: 38px !important; line-height: 1.05 !important; }
        }
      `}</style>

      <div style={{ maxWidth: 880, margin: '0 auto' }}>
        {/* ─── Header ──────────────────────────────────────────────── */}
        <header
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            paddingBottom: 20,
            borderBottom: `1px solid ${T.rule}`,
            marginBottom: 56,
          }}
        >
          <div style={{ fontFamily: fontDisplay, fontSize: 22, letterSpacing: '-0.01em' }}>
            Window Washing <em style={{ fontStyle: 'italic', fontWeight: 400 }}>Guide</em>
          </div>
          <button
            onClick={() => setDark(!dark)}
            aria-label="Toggle dark mode"
            style={{
              background: 'transparent',
              border: `1px solid ${T.rule}`,
              borderRadius: '50%',
              width: 32,
              height: 32,
              color: T.inkSoft,
              cursor: 'pointer',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {dark ? '☀' : '☾'}
          </button>
        </header>

        {/* ─── Tool framing ────────────────────────────────────────── */}
        <div className="reveal reveal-1">
          <div
            style={{
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: '0.18em',
              color: T.sienna,
              marginBottom: 20,
            }}
          >
            § THE TOOLS &nbsp;·&nbsp; 02 / 09 &nbsp;·&nbsp; GEO-AWARE
          </div>

          <h1
            className="h1-title"
            style={{
              fontFamily: fontDisplay,
              fontSize: 60,
              fontWeight: 400,
              lineHeight: 1.04,
              letterSpacing: '-0.025em',
              margin: '0 0 24px',
              maxWidth: 700,
            }}
          >
            Hard Water{' '}
            <em style={{ fontStyle: 'italic', color: T.inkSoft, fontWeight: 400 }}>
              Severity Scorer
            </em>
            .
          </h1>

          <p
            style={{
              fontFamily: fontDisplay,
              fontSize: 21,
              fontWeight: 300,
              lineHeight: 1.5,
              color: T.inkSoft,
              maxWidth: 620,
              margin: '0 0 12px',
            }}
          >
            Enter your ZIP code. We pull regional water hardness data and tell you
            exactly what you're dealing with — and whether you can keep cleaning
            with tap water or need to switch to distilled.
          </p>

          <p
            style={{
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: '0.05em',
              color: T.inkFaint,
              marginBottom: 56,
            }}
          >
            US ZIP codes only. For the 271 covered cities: the utility's own published figure, as on the national rankings. Elsewhere: a planning estimate, labelled as one.
          </p>
        </div>

        {/* ─── Input or Result ─────────────────────────────────────── */}
        {!result ? (
          <div className="reveal reveal-2">
            <div
              style={{
                background: T.surface,
                border: `1px solid ${T.rule}`,
                padding: '48px 44px 40px',
                marginBottom: 40,
              }}
            >
              <label
                htmlFor="zip"
                style={{
                  display: 'block',
                  fontFamily: fontMono,
                  fontSize: 11,
                  letterSpacing: '0.18em',
                  color: T.sienna,
                  marginBottom: 18,
                }}
              >
                ENTER ZIP CODE
              </label>

              <div style={{ display: 'flex', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap' }}>
                <input
                  ref={inputRef}
                  id="zip"
                  className="zip-input"
                  type="text"
                  inputMode="numeric"
                  maxLength={5}
                  size={5}
                  value={zip}
                  onChange={(e) => setZip(e.target.value.replace(/\D/g, ''))}
                  onKeyDown={(e) => e.key === 'Enter' && handleSubmit()}
                  placeholder="85001"
                  style={{
                    flex: '1 1 220px', minWidth: 0, width: '100%', // s135: default input width at 56px overflowed a 390px phone
                    background: 'transparent',
                    border: 'none',
                    borderBottom: `1px solid ${T.ink}`,
                    fontFamily: fontDisplay,
                    fontSize: 56,
                    fontWeight: 300,
                    letterSpacing: '0.04em',
                    color: T.ink,
                    padding: '8px 0 12px',
                    transition: 'border-color 0.2s',
                  }}
                />

                <button
                  className="pill-btn"
                  onClick={handleSubmit}
                  style={{
                    background: 'transparent',
                    border: `1px solid ${T.ink}`,
                    color: T.ink,
                    fontFamily: fontMono,
                    fontSize: 11,
                    letterSpacing: '0.18em',
                    padding: '14px 28px',
                    cursor: 'pointer',
                    transition: 'background 0.2s, color 0.2s',
                  }}
                >
                  SCORE MY WATER →
                </button>
              </div>

              {error && (
                <div
                  style={{
                    marginTop: 20,
                    fontFamily: fontMono,
                    fontSize: 11,
                    letterSpacing: '0.1em',
                    color: T.sienna,
                    whiteSpace: 'normal', overflowWrap: 'anywhere', width: 0, minWidth: '100%', lineHeight: 1.6, // width 0: the message never widens the form
                  }}
                >
                  ◆ {error.length > 40 ? error : error.toUpperCase()}
                  {errLink && <Link to={errLink.to} style={{ display: 'block', marginTop: 8, color: T.sienna }}>Open the {errLink.name} page →</Link>}
                </div>
              )}
            </div>

            {/* Notable cities row */}
            <div className="reveal reveal-3" style={{ marginTop: 48 }}>
              <div
                style={{
                  fontFamily: fontMono,
                  fontSize: 11,
                  letterSpacing: '0.18em',
                  color: T.inkFaint,
                  marginBottom: 16,
                  paddingBottom: 12,
                  borderBottom: `1px solid ${T.rule}`,
                }}
              >
                § REFERENCE — A FEW CITIES, FOR SCALE
              </div>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
                  gap: '20px 32px',
                }}
              >
                {[
                  ['Seattle', 30, 'Soft'],
                  ['New York', 60, 'Soft'],
                  ['San Francisco', 105, 'Mod'],
                  ['Atlanta', 35, 'Soft'],
                  ['Chicago', 225, 'V. Hard'],
                  ['Dallas', 285, 'Ex. Hard'],
                  ['Phoenix', 320, 'Ex. Hard'],
                  ['Las Vegas', 320, 'Ex. Hard'],
                ].map(([city, val, tier]) => (
                  <div key={city} style={{ borderTop: `1px solid ${T.rule}`, paddingTop: 12 }}>
                    <div
                      style={{
                        fontFamily: fontDisplay,
                        fontSize: 18,
                        marginBottom: 4,
                        color: T.ink,
                      }}
                    >
                      {city}
                    </div>
                    <div
                      style={{
                        fontFamily: fontMono,
                        fontSize: 11,
                        color: T.inkFaint,
                        letterSpacing: '0.05em',
                      }}
                    >
                      {val} mg/L · <span style={{ color: T.sienna }}>{tier}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <ResultPanel
            result={result}
            animateNum={animateNum}
            T={T}
            fontDisplay={fontDisplay}
            fontBody={fontBody}
            fontMono={fontMono}
            onReset={reset}
            cameFromDeepLink={cameFromDeepLink}
          />
        )}

        {/* ─── Footer ──────────────────────────────────────────────── */}
        <footer
          style={{
            marginTop: 80,
            paddingTop: 24,
            borderTop: `1px solid ${T.rule}`,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            flexWrap: 'wrap',
            gap: 16,
            fontFamily: fontMono,
            fontSize: 11,
            color: T.inkFaint,
            letterSpacing: '0.1em',
          }}
        >
          <span>NO. 042 &nbsp;·&nbsp; TOOL 02 OF 09</span>
          <Link
            to="/tools/hard-water-scorer/guide"
            style={{
              color: T.sienna,
              textDecoration: 'none',
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: '0.1em',
            }}
          >
            READ THE METHODOLOGY →
          </Link>
        </footer>
      </div>
    </div>
  );
}

// ─── Result panel (separate for clarity) ─────────────────────────────────
function ResultPanel({ result, animateNum, T, fontDisplay, fontBody, fontMono, onReset, cameFromDeepLink }) {
  const { mgL, gpg, classification: cls, city, precision, recommendation, zip } = result;
  const tierLabels = ['Soft', 'Moderate', 'Hard', 'Very Hard', 'Extremely Hard'];
  const tierMaxes = [60, 120, 180, 250, 400];

  // Position on the scale (0..1) for the bar fill — capped at 400 mg/L.
  const scalePos = Math.min(mgL / 400, 1);

  return (
    <div>
      {/* ─── Deep-link attribution chip ──────────────────────────────
          Renders only when the visitor arrived via a ?zip= deep-link
          from a state or city page. Gives them a one-tap exit out of
          the prefilled state — useful when they clicked "score this
          city" from a state page just to peek at the number, but
          actually want to enter their own ZIP. Without this chip the
          deep-link is one-way: you land on a result with no signal
          of where it came from and no obvious affordance to clear it.
          Manual scans and resets clear the flag in the parent. */}
      {cameFromDeepLink && (
        <div
          className="reveal reveal-1"
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16,
            padding: '10px 14px',
            marginBottom: 28,
            border: `1px solid ${T.rule}`,
            background: T.surface,
            fontFamily: fontMono,
            fontSize: 10,
            letterSpacing: '0.08em',
            color: T.inkSoft,
          }}
        >
          <span style={{ textTransform: 'uppercase' }}>
            Result prefilled from ZIP <span style={{ color: T.ink }}>{zip}</span>
          </span>
          <button
            type="button"
            onClick={onReset}
            style={{
              border: 'none',
              background: 'transparent',
              padding: 0,
              cursor: 'pointer',
              fontFamily: fontMono,
              fontSize: 10,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              color: T.sienna,
            }}
            aria-label="Clear deep-link and open this tool fresh"
          >
            Open the tool fresh →
          </button>
        </div>
      )}

      {/* ─── Top: location + score ───────────────────────────────── */}
      <div className="reveal reveal-1">
        <div
          style={{
            fontFamily: fontMono,
            fontSize: 11,
            letterSpacing: '0.18em',
            color: T.sienna,
            marginBottom: 16,
          }}
        >
          ◆ DIAGNOSIS · ZIP {zip}
        </div>

        <div
          className="grid-2col"
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 40,
            alignItems: 'end',
            paddingBottom: 32,
            borderBottom: `1px solid ${T.rule}`,
            marginBottom: 40,
          }}
        >
          <div>
            <div
              style={{
                fontFamily: fontMono,
                fontSize: 11,
                letterSpacing: '0.1em',
                color: T.inkFaint,
                marginBottom: 8,
              }}
            >
              YOUR REGION
            </div>
            <div
              style={{
                fontFamily: fontDisplay,
                fontSize: 32,
                fontWeight: 400,
                lineHeight: 1.1,
                letterSpacing: '-0.02em',
              }}
            >
              {city}
            </div>
            <div
              style={{
                fontFamily: fontMono,
                fontSize: 10,
                letterSpacing: '0.1em',
                color: T.inkFaint,
                marginTop: 8,
              }}
            >
              {precision === 'published' ? `PUBLISHED BY ${String(result.by || 'THE UTILITY').toUpperCase()}` : precision === 'estimate' ? 'ESTIMATE · NOT A UTILITY FIGURE' : 'REGIONAL ESTIMATE · NOT A UTILITY FIGURE'}
            </div>
          </div>

          <div style={{ textAlign: 'right' }}>
            <div
              className="display-num"
              style={{
                fontFamily: fontDisplay,
                fontSize: 132,
                fontWeight: 300,
                lineHeight: 0.9,
                letterSpacing: '-0.04em',
                color: T.ink,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {result.display || animateNum}
            </div>
            <div
              style={{
                fontFamily: fontMono,
                fontSize: 11,
                letterSpacing: '0.18em',
                color: T.inkFaint,
                marginTop: 4,
              }}
            >
              MG/L CACO₃ &nbsp;·&nbsp; {gpg} GPG
            </div>
          </div>
        </div>
      </div>

      {/* ─── Classification + scale ──────────────────────────────── */}
      <div className="reveal reveal-2" style={{ marginBottom: 56 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            gap: 18,
            marginBottom: 28,
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              fontFamily: fontMono,
              fontSize: 11,
              letterSpacing: '0.18em',
              color: T.inkFaint,
            }}
          >
            CLASSIFICATION
          </div>
          <div
            style={{
              fontFamily: fontDisplay,
              fontSize: 36,
              fontWeight: 400,
              lineHeight: 1,
              letterSpacing: '-0.02em',
            }}
          >
            <em style={{ fontStyle: 'italic', color: T.sienna, fontWeight: 400 }}>
              {cls.label}
            </em>
          </div>
        </div>

        {/* Scale bar */}
        <div style={{ position: 'relative', marginTop: 20 }}>
          <div
            style={{
              height: 1,
              background: T.rule,
              position: 'relative',
              marginBottom: 14,
            }}
          >
            <div
              className="bar-fill"
              style={{
                position: 'absolute',
                top: -1,
                left: 0,
                height: 3,
                width: `${scalePos * 100}%`,
                background: T.sienna,
              }}
            />
            <div
              className="bar-fill"
              style={{
                position: 'absolute',
                top: -5,
                left: `${scalePos * 100}%`,
                width: 1,
                height: 11,
                background: T.ink,
                transformOrigin: 'top',
              }}
            />
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(5, 1fr)',
              gap: 4,
            }}
          >
            {tierLabels.map((label, i) => (
              <div
                key={label}
                className={`scale-tier ${cls.tier === i ? 'active' : 'dim'}`}
                style={{
                  fontFamily: fontMono,
                  fontSize: 10,
                  letterSpacing: '0.08em',
                  color: cls.tier === i ? T.ink : T.inkFaint,
                  textAlign: i === 0 ? 'left' : i === 4 ? 'right' : 'center',
                }}
              >
                <div>{label.toUpperCase()}</div>
                <div style={{ marginTop: 2, color: T.inkFaint }}>
                  {i === 0 ? '0' : tierMaxes[i - 1] + 1}
                  {i < 4 ? `–${tierMaxes[i]}` : '+'}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ─── Recommendation ──────────────────────────────────────── */}
      <div
        className="reveal reveal-3"
        style={{
          background: T.surface,
          borderLeft: `2px solid ${T.sienna}`,
          padding: '36px 40px',
          marginBottom: 40,
        }}
      >
        <div
          style={{
            fontFamily: fontMono,
            fontSize: 11,
            letterSpacing: '0.18em',
            color: T.sienna,
            marginBottom: 18,
          }}
        >
          ⚡ THE WORKING ANSWER
        </div>

        <h2
          style={{
            fontFamily: fontDisplay,
            fontSize: 30,
            fontWeight: 400,
            lineHeight: 1.15,
            letterSpacing: '-0.02em',
            margin: '0 0 16px',
            color: T.ink,
          }}
        >
          {recommendation.headline}
        </h2>

        <p
          style={{
            fontFamily: fontDisplay,
            fontSize: 17,
            fontWeight: 300,
            lineHeight: 1.65,
            color: T.inkSoft,
            margin: '0 0 28px',
          }}
        >
          {recommendation.summary}
        </p>

        <div style={{ borderTop: `1px solid ${T.rule}` }}>
          {recommendation.actions.map((a, i) => (
            <div
              key={a.k}
              style={{
                display: 'grid',
                gridTemplateColumns: '160px 1fr',
                gap: 20,
                padding: '14px 0',
                borderBottom: `1px solid ${T.rule}`,
                fontSize: 14,
              }}
            >
              <div
                style={{
                  fontFamily: fontMono,
                  fontSize: 10,
                  letterSpacing: '0.12em',
                  color: T.inkFaint,
                  textTransform: 'uppercase',
                  paddingTop: 2,
                }}
              >
                № 0{i + 1} &nbsp; {a.k}
              </div>
              <div
                style={{
                  fontFamily: fontBody,
                  color: T.ink,
                  lineHeight: 1.5,
                }}
              >
                {a.v}
              </div>
            </div>
          ))}
        </div>

        <div
          style={{
            marginTop: 20,
            paddingTop: 16,
            fontFamily: fontMono,
            fontSize: 11,
            letterSpacing: '0.05em',
            color: T.inkSoft,
          }}
        >
          ◆ {recommendation.flag}
        </div>
      </div>

      {/* ─── Cross-links ─────────────────────────────────────────── */}
      <div
        className="reveal reveal-4"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 0,
          borderTop: `1px solid ${T.rule}`,
          marginBottom: 40,
        }}
      >
        {[
          ['DIAGNOSTICIAN', 'Why your windows look worse after you clean them', '/articles/why-windows-look-worse'],
          ['ENCYCLOPEDIA', 'Hard water spots, every method ranked', '/articles/hard-water-spots-ranked'],
          ['THE TOOLS', 'The Solution Calculator →', '/tools/solution-calculator'],
        ].map(([kicker, title, to]) => (
          <Link
            key={title}
            to={to}
            className="ghost-btn"
            style={{
              padding: '20px 0',
              borderBottom: `1px solid ${T.rule}`,
              textDecoration: 'none',
              color: T.ink,
              transition: 'color 0.2s',
            }}
          >
            <div
              style={{
                fontFamily: fontMono,
                fontSize: 10,
                letterSpacing: '0.18em',
                color: T.sienna,
                marginBottom: 8,
              }}
            >
              {kicker}
            </div>
            <div
              style={{
                fontFamily: fontDisplay,
                fontSize: 17,
                lineHeight: 1.3,
                paddingRight: 16,
              }}
            >
              {title}
            </div>
          </Link>
        ))}
      </div>

      {/* ─── Reset ────────────────────────────────────────────────── */}
      <div className="reveal reveal-5" style={{ textAlign: 'center', marginTop: 32 }}>
        <button
          className="ghost-btn"
          onClick={onReset}
          style={{
            background: 'transparent',
            border: 'none',
            color: T.inkSoft,
            fontFamily: fontMono,
            fontSize: 11,
            letterSpacing: '0.18em',
            cursor: 'pointer',
            padding: '12px 0',
            transition: 'color 0.2s',
          }}
        >
          ← TRY ANOTHER ZIP
        </button>
      </div>
    </div>
  );
}
